<script setup>
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import MultiSelect from '../ui/MultiSelect.vue';
import { ElButton, ElDialog, ElMessage } from 'element-plus';
import axios, { HttpStatusCode } from 'axios';
import { serviceName } from '../../utils/strings';

const props = defineProps({
    product: Object,
    toolReference: String,
    mappings: Array,
    service: String,
});

const queryClient = useQueryClient();

const showDialog = ref(false);
// Intelliflo has no catalogue call: it answers one provider at a time, and a full fetch is slow enough
// to be opt-in. Only one Intelliflo product can be mapped to a PGP provider.
const isIntellifloService = computed(() => props.service === 'INTELLIFLO');
const fetchAll = ref(false);

// Some services list products per tool (Iress records what each tool's quotes returned), others globally.
const productsQueryKey = computed(() => (
    isIntellifloService.value
        ? ['pricing-products', props.service, props.toolReference, props.product?.providerName, fetchAll.value]
        : ['pricing-products', props.service, props.toolReference]
));

function getProductsUrl() {
    const baseUrl = `api/Configuration/ListPricingProducts/${props.service}`;
    if (isIntellifloService.value) {
        return `${baseUrl}/${encodeURIComponent(props.toolReference || '')}/byProvider?providerName=${encodeURIComponent(props.product?.providerName || '')}&fetchAll=${fetchAll.value}`;
    }
    return `${baseUrl}?toolReference=${encodeURIComponent(props.toolReference || '')}`;
}

const {
    data: products,
    isFetching: isFetchingProducts,
} = useQuery({
    queryKey: productsQueryKey,
    enabled: computed(() => (isIntellifloService.value ? showDialog.value : true)),
    queryFn: () => axios(getProductsUrl()).then(res => res.data),
});


const options = computed(() => {
    const mappedProducts = isIntellifloService.value ? [] : (props.mappings?.filter(m => m.apiProductId != props.product.providerReference)
        .flatMap(m => m.externalIds) || []);
    return products.value?.map(product => ({
        key: String(product.productId),
        label: product.productId  + " - " + product.productName,
        disabled: mappedProducts.includes(String(product.productId))
    })) || []
})

const mapping = computed(() => props.mappings?.find(m => m.apiProductId == props.product.providerReference));
const value = ref(mapping?.value?.externalIds || []);
const mapCount = computed(() => mapping.value?.externalIds?.length || 0);

watch(() => mapping.value, (newValue) => {
    const next = newValue?.externalIds || [];
    value.value = isIntellifloService.value ? next.slice(0, 1) : next;
})

const { mutate, isLoading: isSaving } = useMutation({
    mutationFn: async (request) => {
        try {
            const response = await axios.post('api/Configuration/SaveProductMapping', JSON.stringify(request), {
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json;charset=UTF-8',
                },
            });

            if (response.status === HttpStatusCode.Ok) {
                return response.data;
            } else {
                throw new Error(response.statusText);
            }
        } catch (error) {
            throw new Error(error.message);
        }
    },
    onSuccess: () => {
        ElMessage.success('Mapping saved.');
        queryClient.invalidateQueries({ queryKey: ['tool-mappings', props.service, props.toolReference] });
        showDialog.value = false;
    },
    onError: () => {
        ElMessage.error('Mapping could not be saved');
    }
});

function saveMappings() {
    mutate({
        serviceType: props.service,
        apiProductId: props.product.providerReference,
        toolReference: props.toolReference,
        externalIds: isIntellifloService.value ? value.value.slice(0, 1) : value.value,
    });
}

// A full Intelliflo fetch replaces the product list, so hold the selection across the refetch.
const savedValue = ref([]);

watch(showDialog, (isOpen) => {
    if (isOpen && isIntellifloService.value) {
        fetchAll.value = false;
    }
});

function handleFetchAll() {
    savedValue.value = [...value.value];
    fetchAll.value = true;
}

watch(products, (newProducts) => {
    if (fetchAll.value && savedValue.value.length && newProducts?.length) {
        value.value = [...savedValue.value];
        savedValue.value = [];
    }
});

function handleMaxReached() {
    ElMessage.warning('Only one product can be mapped for Intelliflo.');
}

const titles = { left: serviceName(props.service) + " products", right: props.product.providerName };
</script>
<template>
    <div class="row">
        <div class="title">{{ product.providerName }}</div>
        <div class="subtitle">
            <span>{{ product.providerReference }}</span>
            <span class="mapping" @click="showDialog = true">{{ mapCount }} {{ mapCount == 1 ? 'product' : 'products' }}
                mapped</span>
        </div>
    </div>
    <ElDialog v-model="showDialog" destroy-on-close>
        <p class="tip">Select the products from {{ serviceName(service) }} to be mapped to <strong>{{ product.providerName }}</strong></p>
        <p v-if="service === 'IRESS' && !options.length" class="tip">
            No Iress products have been seen for this tool yet. They appear here after the first Exchange quote (or connectivity check) for the tool.
        </p>
        <span v-if="isIntellifloService && !fetchAll" class="fetch-all-link" @click="handleFetchAll">
            Fetch all products
        </span>
        <div v-if="isIntellifloService && fetchAll && isFetchingProducts" class="loading-banner">
            Loading all products, this may take a moment...
        </div>
        <MultiSelect
            v-model="value"
            :data="options"
            :titles="titles"
            :max-selected="isIntellifloService ? 1 : null"
            @max-reached="handleMaxReached"
        />
        <template #footer>
            <div class="dialog-footer">
                <ElButton type="primary" link @click="showDialog = false">Cancel</ElButton>
                <ElButton type="primary" :loading="isSaving" @click="saveMappings">Save and close</ElButton>
            </div>
        </template>
    </ElDialog>
</template>
<style scoped>
.fetch-all-link {
    color: var(--el-color-primary);
    cursor: pointer;
    text-decoration: underline;
    font-size: 0.9rem;
}

.loading-banner {
    margin: 0.5rem 0;
    font-size: 0.9rem;
    color: #7a5b00;
}

.row {
    max-width: 100%;
    padding: 0.5rem;
    border-radius: 0.5rem;
}

.title {
    font-weight: bold;
}

.subtitle {
    display: flex;
    font-size: small;
    gap: 1rem
}

.subtitle .mapping {
    color: var(--el-color-primary);
    text-decoration: underline;
    cursor: pointer;
}

.dialog-footer {
    display: inline-flex;
    gap: 2rem;
}

.dialog-footer button {
    width: unset;
}
</style>